import { html, svg, type TemplateResult } from 'lit-html';
import { ref } from 'lit-html/directives/ref.js';
import { app } from './app.js';

export interface BarPoint {
  label: string;
  /** values[0] is series 1 (full bar); values[1], if present, series 2 (narrower bar inside it). */
  values: number[];
  tip: TemplateResult;
}

const hovered = new Map<string, number | null>();

export const plural = (n: number, word: string) => `${n.toLocaleString()} ${word}${n === 1 ? '' : 's'}`;

/**
 * Vertical bars, one y-scale. Colors are Catch chart slots 1 and 2, in order. `id` keeps each
 * chart's hover state apart when a page shows several.
 */
export function barChart(id: string, points: BarPoint[], ariaLabel: string): TemplateResult {
  const W = 880, H = 180, P = { l: 32, r: 8, t: 12, b: 24 };
  const max = Math.max(1, ...points.flatMap((d) => d.values));
  const step = (W - P.l - P.r) / Math.max(1, points.length);
  const bw = Math.max(2, step * 0.7);
  const y = (v: number) => P.t + (H - P.t - P.b) * (1 - v / max);
  const ticks = [...new Set([0, Math.ceil(max / 2), max])];
  const labelEvery = Math.ceil(points.length / 12);
  const current = hovered.get(id) ?? null;
  const hover = (i: number | null) => {
    hovered.set(id, i);
    app.rerender();
  };
  const tip = current === null ? null : points[current];
  // Keep the tooltip inside the chart: below the point near the top, anchored inward near either edge.
  const tipClass = (i: number, top: number) =>
    ['chart-tip', top < 0.35 ? 'below' : '', i < points.length * 0.15 ? 'from-left' : i > points.length * 0.85 ? 'from-right' : ''].join(' ');
  return html`<div class="chart-wrap" @mouseleave=${() => hover(null)}>
    ${svg`<svg viewBox="0 0 ${W} ${H}" class="chart" role="img" aria-label=${ariaLabel}>
      ${ticks.map((t) => svg`<line x1=${P.l} x2=${W - P.r} y1=${y(t)} y2=${y(t)} class=${t === 0 ? 'axis' : 'grid'}/><text x=${P.l - 6} y=${y(t) + 4} class="tick" text-anchor="end">${t}</text>`)}
      ${points.map((d, i) => {
        const x = P.l + i * step + (step - bw) / 2;
        const [a = 0, b] = d.values;
        return svg`<g class=${current === i ? 'day hover' : 'day'} @mouseenter=${() => hover(i)}>
          <rect x=${P.l + i * step} y=${P.t} width=${step} height=${H - P.t - P.b} class="hit"/>
          <rect x=${x} y=${y(a)} width=${bw} height=${y(0) - y(a)} class="bar-1"/>
          ${b === undefined ? '' : svg`<rect x=${x + bw * 0.2} y=${y(b)} width=${bw * 0.6} height=${y(0) - y(b)} class="bar-2"/>`}
          ${i % labelEvery === 0 ? svg`<text x=${x + bw / 2} y=${H - 6} class="tick" text-anchor="middle">${d.label}</text>` : ''}</g>`;
      })}
    </svg>`}
    ${tip
      ? html`<div class=${tipClass(current!, y(tip.values[0] ?? 0) / H)} ${ref((el) => {
          // Positioned through the CSSOM: the CSP (style-src 'self') blocks style attributes.
          if (!(el instanceof HTMLElement)) return;
          el.style.left = `${((P.l + (current! + 0.5) * step) / W) * 100}%`;
          el.style.top = `${(y(tip.values[0] ?? 0) / H) * 100}%`;
        })}>${tip.tip}</div>`
      : ''}
  </div>`;
}

export interface CompareRow {
  label: string;
  /** Shares (0–1) for series 1 and series 2. */
  a: number;
  b: number;
}

/** Horizontal paired bars comparing two distributions (series 1 above series 2), with percentages. */
export function compareBars(rows: CompareRow[], names: [string, string], ariaLabel: string): TemplateResult {
  const W = 880, LABEL = 132, PCT = 120, ROW = 34, BAR = 11;
  const H = rows.length * ROW;
  const max = Math.max(0.0001, ...rows.flatMap((r) => [r.a, r.b]));
  const len = (v: number) => ((W - LABEL - PCT) * v) / max;
  const pct = (v: number) => `${(v * 100).toFixed(v > 0 && v < 0.1 ? 1 : 0)}%`;
  return html`${svg`<svg viewBox="0 0 ${W} ${H}" class="chart" role="img" aria-label=${ariaLabel}>
    ${rows.map((r, i) => {
      const top = i * ROW + 4;
      return svg`<g>
        <text x="0" y=${top + BAR + 4} class="tick">${r.label}</text>
        <rect x=${LABEL} y=${top} width=${len(r.a)} height=${BAR} class="bar-1"><title>${names[0]}: ${pct(r.a)}</title></rect>
        <rect x=${LABEL} y=${top + BAR + 2} width=${len(r.b)} height=${BAR} class="bar-2"><title>${names[1]}: ${pct(r.b)}</title></rect>
        <text x=${W} y=${top + BAR + 4} class="tick" text-anchor="end">${pct(r.a)} · ${pct(r.b)}</text>
      </g>`;
    })}
  </svg>`}`;
}
